define('core/switches',[
    'underscore',
    'remote/config',
    'core/analytics/identity',
    'core/utils/storage',
    'core/utils/auth',
], function (
    _,
    config,
    identity,
    storage,
    auth
) {
    'use strict';

    /**
     * A pretty simple module that buckets users into features switches based
     * on their client percentage.
    */

    var SWITCH_NAMESPACE = 'switch:';

    var overrides = {};

    var exports = {};


    exports._getKey = function (switchName) {
        return SWITCH_NAMESPACE + switchName;
    };

    /**
     * Disable a feature for this client.
     *
     * @param {string} switchName - Name of the switch to be disabled
     */
    exports.disableFeature = function (switchName) {
        overrides[switchName] = false;
    };

    /**
     * Unset the feature preference for this client.
     * Feature will default to global setting.
     *
     * @param {string} switchName - Name of the switch to be unset
     */
    exports.resetFeature = function (switchName) {
        overrides[switchName] = null;
    };

    /**
     * Enable a feature for this client.
     *
     * @param {string} switchName - Name of the switch to be enabled
     */
    exports.forceFeature = function (switchName) {
        overrides[switchName] = true;
    };

    exports.getSwitchContext = function (switchName) {
        var localOverride = storage.get(this._getKey(switchName));

        // localSettings may be False, which should be preferred over remote config
        if (localOverride !== null)
            return localOverride;

        var customOverride = overrides[switchName];

        // customOverride may be False, which should be preferred over remote config
        if (customOverride != null)  // eslint-disable-line no-eq-null
            return customOverride;

        return (config.lounge && config.lounge.switches || {})[switchName];
    };

    /**
     * Checks if a given switch is active for this client.
     *
     * What will isFeatureActive('myFeature') return?
     *
     * Follow list below in order, stop at first matching line.
     *
     * standard values, in local settings: ex: boolean, percent, user_id, username, is_staff
     *
     * forced features
     *
     * Features forced "on" via `forceFeature` method, only for this
     * specific instance of this client. This is used for cases like
     * where an experiments needs to be activated, eg in the embed.
     * Note: experiments set through tempest use snake case
     *
     * my_experiment not in Jones
     * > isFeatureActive('experiment:my_experiment:active') -> false
     * > forceFeature('experiment:my_experiment:active')
     * > isFeatureActive('experiment:my_experiment:active') -> true
     *
     * standard values, in Jones: ex: boolean, percent, user_id, username, is_staff
     *
     *     myFeature not in jones false
     *     myFeature: true/false
     *     myFeature: { percent: 10 }
     *             and user's percent < 10 true
     *     myFeature: { percent: 0 } false
     *     myFeature: { percent: 100 } true
     *     myFeature: { user_id: 123 } or myFeature: { user_id: [..., 123, ...] }
     *             and user's id is 123 true
     *     myFeature: { username: 'nicoleallard' }
     *             or myFeature: { username: [..., 'nicoleallard', ...] }
     *             and user's username is nicoleallard true
     *     myFeature: { is_staff: true }
     *             and user is staff true
     *     myFeature: { is_staff: false }
     *             and user is not staff true
     *
     * examples of custom Jones values: forum, privateChannel, etc
     *
     *     myFeature: { forum: 'talkshop' }
     *             and isFeatureActive('myFeature', { forum: 'talkshop' }) true
     *     myFeature: { privateChannel: ['disqusfun', 'gabalafou-test'] }
     *             and isFeatureActive('myFeature', { privateChannel: 'disqusfun' }) true
     *     myFeature: { forum_percent: 82 }
     *             and isFeatureActive('myFeature', { forum_percent: 'talkshop' }) true
     *             above is true, only if the string `talkshop` falls in to the 82nd percentile
     *     otherwise false
     *
     * @param {string} switchName - Name of switch to be checked
     * @param {Object} extraContext - Values that switch is checked against
     * @returns {boolean}
     */
    exports.isFeatureActive = function (switchName, extraContext) {
        var switchConfig = exports.getSwitchContext(switchName);

        if (_.isBoolean(switchConfig))
            return switchConfig;

        if (!switchConfig)
            return false;

        var disqusauth = auth.getFromCookie();
        var userContext = {
            percent: identity.clientPercent(),
            user_id: disqusauth.id,
            username: disqusauth.username,
            is_staff: disqusauth.staff,
            is_moderator: disqusauth.isModerator,
        };

        var context = _.defaults(
            extraContext || {},
            userContext
        );

        return _.any(switchConfig, function (val, key) {
            var contextVal = context[key];

            if (/percent$/.test(key) && _.isNumber(val)) {  // percentage
                if (_.isNumber(contextVal))
                    return val > contextVal;

                if (_.isString(contextVal))
                    return val > identity.getPercentBucketForString(contextVal);

                return false;
            }

            if (_.isArray(val))  // list of items / enum
                return _.contains(val, contextVal);

            // Fall back to exact matching if none of the above
            return val === contextVal;
        });
    };

    return exports;
});

